import { cn } from "@/utils/cn.ts";
import { useOneaiMarkSrc } from "@/hooks/use-oneai-mark.ts";

interface MotionLoaderProps {
	text?: string;
	size?: "sm" | "md" | "lg";
	className?: string;
	containerClassName?: string;
}

const sizeMap = {
	sm: { container: "h-6 w-6", dot: "h-2 w-2", icon: "h-8 w-8" },
	md: { container: "h-8 w-8", dot: "h-3 w-3", icon: "h-12 w-12" },
	lg: { container: "h-12 w-12", dot: "h-4 w-4", icon: "h-16 w-16" },
};

/**
 * Smooth, modern loading animation using pure CSS
 * Lightweight alternative to Motion library
 */
export function MotionLoader({
	text,
	size = "md",
	className,
	containerClassName,
}: MotionLoaderProps) {
	const dimensions = sizeMap[size];
	const markSrc = useOneaiMarkSrc();

	return (
		<div className={cn("flex flex-col items-center gap-3", containerClassName)}>
			<img
				src={markSrc}
				alt="Loading"
				className={cn(dimensions.icon, "animate-logo-rotate", className)}
			/>
			{text && (
				<p className="text-sm text-muted-foreground animate-text-pulse">
					{text}
				</p>
			)}
		</div>
	);
}

/**
 * Full-screen loading overlay variant
 */
export function MotionLoaderFullscreen({
	text,
	size = "lg",
	className,
}: MotionLoaderProps) {
	return (
		<div
			className={cn(
				"fixed inset-0 z-50 flex items-center justify-center",
				"bg-background/80 backdrop-blur-sm",
				"animate-in fade-in duration-200",
				className,
			)}
		>
			<MotionLoader
				text={text}
				size={size}
				containerClassName="flex flex-col items-center"
			/>
		</div>
	);
}
